import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, Play, Save, Settings, Trash2, Users, Database, Zap, MessageSquare, GitBranch, Edit3, Eye, Bug, AlertTriangle, CheckCircle, Clock, X, Calculator, FileText, TrendingUp } from 'lucide-react';
import styles from './agents.module.css';

export function Agents() {
  const [agents, setAgents] = useState([]);
  const [showAgentBuilder, setShowAgentBuilder] = useState(false);
  const [activeTab, setActiveTab] = useState('builder'); // builder, runs, debug, context
  const [draggedAction, setDraggedAction] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCustomDragging, setIsCustomDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);

  // Enhanced agent builder state
  const [agentConfig, setAgentConfig] = useState({
    name: '',
    description: '',
    trigger: null,
    actions: [],
    humanInLoop: {
      enabled: false,
      checkpoints: [],
      approvalRequired: false,
      notificationEmail: ''
    },
    context: {
      clientData: true,
      financialPeriod: 'current',
      taxYear: '2024',
      practiceAreas: []
    }
  });

  const [sampleRuns, setSampleRuns] = useState([]);
  const [debugLog, setDebugLog] = useState([]);

  // Custom drag handler functions
  const handleCustomDragMove = useCallback((e) => {
    if (!isCustomDragging || !draggedAction) return;
    
    // Update cursor
    document.body.style.cursor = 'grabbing';
    
    // Check if we're over the canvas
    if (canvasRef.current) {
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const isOverCanvas = (
        e.clientX >= canvasRect.left &&
        e.clientX <= canvasRect.right &&
        e.clientY >= canvasRect.top &&
        e.clientY <= canvasRect.bottom
      );
      
      if (isOverCanvas !== isDragOver) {
        setIsDragOver(isOverCanvas);
      }
    }
  }, [isCustomDragging, draggedAction, isDragOver]);

  const handleCustomDragEnd = useCallback((e) => {
    if (!isCustomDragging || !draggedAction) return;
    
    // Reset visual state
    document.body.style.cursor = '';
    setIsCustomDragging(false);
    
    // Check if we're dropping on the canvas
    if (canvasRef.current) {
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const isOverCanvas = (
        e.clientX >= canvasRect.left &&
        e.clientX <= canvasRect.right &&
        e.clientY >= canvasRect.top &&
        e.clientY <= canvasRect.bottom
      );
      
      if (isOverCanvas) {
        const x = Math.max(20, e.clientX - canvasRect.left - dragOffset.x);
        const y = Math.max(20, e.clientY - canvasRect.top - dragOffset.y);
        
        const newAction = {
          id: draggedAction.id + '_' + Date.now(),
          type: draggedAction.id,
          name: draggedAction.name,
          position: { x, y },
          config: { ...draggedAction.template },
          humanCheckpoint: false,
        };
        
        setAgentConfig(prev => ({
          ...prev,
          actions: [...prev.actions, newAction]
        }));
      }
    }
    
    // Reset states
    setDraggedAction(null);
    setIsDragOver(false);
  }, [isCustomDragging, draggedAction, dragOffset]);

  // Global mouse event handlers for custom drag
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isCustomDragging) {
        handleCustomDragMove(e);
      }
    };

    const handleGlobalMouseUp = (e) => {
      if (isCustomDragging) {
        handleCustomDragEnd(e);
      }
    };

    if (isCustomDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isCustomDragging, handleCustomDragMove, handleCustomDragEnd]);

  // Accounting-specific triggers
  // const triggerTypes = [
  //   { id: 'schedule', name: 'Schedule', icon: '⏰', description: 'Run on recurring schedule (month-end, quarter-end)', category: 'timing' },
  //   { id: 'manual', name: 'On Demand', icon: '🚀', description: 'Manual trigger for ad-hoc analysis', category: 'timing' },
  //   { id: 'qbo_transaction', name: 'QBO Transaction', icon: '💰', description: 'New transactions requiring classification', category: 'accounting' },
  //   { id: 'xero_bankrec', name: 'Xero Bank Rec', icon: '🏦', description: 'Bank reconciliation discrepancies', category: 'accounting' },
  //   { id: 'tax_deadline', name: 'Tax Deadline', icon: '📅', description: 'Approaching tax deadlines', category: 'tax' },
  //   { id: 'audit_flag', name: 'Audit Flags', icon: '🔍', description: 'Unusual transactions or patterns', category: 'compliance' },
  //   { id: 'client_portal', name: 'Client Upload', icon: '📤', description: 'Client document uploads', category: 'client' }
  // ];

  // Accounting-specific actions with enhanced LLM capabilities
  const actionTypes = [
    { 
      id: 'client_input', 
      name: 'Client Input Request', 
      icon: MessageSquare, 
      description: 'Request info from clients',
      category: 'client',
      template: { fields: [], message: '', deadline: '' }
    },
    { 
      id: 'retrieve_financial', 
      name: 'Retrieve Financial Data', 
      icon: Database, 
      description: 'Pull P&L, Balance Sheet, Trial Balance',
      category: 'data',
      template: { source: '', reportType: '', dateRange: '' }
    },
    { 
      id: 'ai_analysis', 
      name: 'AI Financial Analysis', 
      icon: Zap, 
      description: 'Advanced AI analysis with custom prompts',
      category: 'ai',
      template: { 
        model: 'claude-sonnet-4',
        systemPrompt: '',
        userPrompt: '',
        temperature: 0.1,
        outputFormat: 'structured'
      }
    },
    { 
      id: 'tax_calculation', 
      name: 'Tax Calculations', 
      icon: Calculator, 
      description: 'Automated tax computations',
      category: 'tax',
      template: { taxYear: '', jurisdiction: '', calculations: [] }
    },
    { 
      id: 'transaction_update', 
      name: 'Update Transactions', 
      icon: Edit3, 
      description: 'Bulk update transaction classifications',
      category: 'accounting',
      template: { updateType: '', filters: '', newValues: {} }
    },
    { 
      id: 'compliance_check', 
      name: 'Compliance Verification', 
      icon: CheckCircle, 
      description: 'Check regulatory compliance',
      category: 'compliance',
      template: { standards: [], reportType: '' }
    },
    { 
      id: 'generate_report', 
      name: 'Generate Report', 
      icon: FileText, 
      description: 'Create client reports and summaries',
      category: 'output',
      template: { template: '', format: 'pdf', recipients: [] }
    },
    { 
      id: 'human_review', 
      name: 'Human Review Point', 
      icon: Users, 
      description: 'Pause for professional review',
      category: 'workflow',
      template: { reviewType: '', instructions: '', assignee: '' }
    }
  ];

  // Canned AI prompts for accounting
  const cannedPrompts = {
    'expense_classification': {
      name: 'Expense Classification',
      prompt: `Analyze the following transactions and classify them according to standard accounting categories. For each transaction, provide:
1. Suggested GL account
2. Confidence level (1-10)
3. Any additional notes or flags

Transactions: {{transactions}}

Consider the client's industry: {{industry}}
Chart of accounts: {{chart_of_accounts}}`
    },
    'variance_analysis': {
      name: 'Budget Variance Analysis',
      prompt: `Perform a detailed variance analysis comparing actual vs budget figures. Identify:
1. Significant variances (>10% or >$1,000)
2. Possible explanations for variances
3. Recommendations for management

Actual figures: {{actual_data}}
Budget figures: {{budget_data}}
Prior period: {{prior_period}}`
    },
    'audit_risk_assessment': {
      name: 'Audit Risk Assessment',
      prompt: `Evaluate the following financial data for audit risks and unusual patterns. Flag:
1. Transactions outside normal ranges
2. Round number patterns
3. Timing anomalies
4. Related party transactions

Financial data: {{financial_data}}
Industry benchmarks: {{benchmarks}}
Prior year data: {{prior_year}}`
    },
    'tax_planning': {
      name: 'Tax Planning Analysis',
      prompt: `Analyze the current financial position and recommend tax planning strategies:
1. Income deferral opportunities
2. Deduction acceleration possibilities
3. Entity structure optimization
4. Estimated tax payment planning

Current financials: {{current_financials}}
Tax year: {{tax_year}}
Client goals: {{client_goals}}`
    }
  };


  // Custom mouse-based drag handlers as fallback
  const handleCustomDragStart = (e, action) => {
    setDraggedAction(action);
    setIsCustomDragging(true);
    
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    
    // Add visual feedback
    e.currentTarget.style.opacity = '0.6';
    e.currentTarget.style.transform = 'rotate(2deg) scale(1.05)';
    
    // Prevent text selection
    e.preventDefault();
  };



  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Ensure we can drop here
    if (e.dataTransfer.types.includes('application/json') || e.dataTransfer.types.includes('text/plain')) {
      e.dataTransfer.dropEffect = 'copy';
    }
    
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set dragOver to false if we're leaving the canvas container
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    let actionData = draggedAction;
    
    // Try to get action data from multiple sources
    if (!actionData) {
      try {
        const jsonData = e.dataTransfer.getData('application/json');
        if (jsonData) {
          actionData = JSON.parse(jsonData);
        }
      } catch (err) {
        // Could not parse JSON data
      }
    }
    
    if (!actionData) {
      const actionId = e.dataTransfer.getData('text/plain');
      actionData = actionTypes.find(a => a.id === actionId);
    }
    
    if (actionData && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = Math.max(20, e.clientX - rect.left - 120); // Center the action node
      const y = Math.max(20, e.clientY - rect.top - 40);
      
      const newAction = {
        id: actionData.id + '_' + Date.now(),
        type: actionData.id,
        name: actionData.name,
        position: { x, y },
        config: { ...actionData.template },
        humanCheckpoint: false,
        completed: false
      };
      
      setAgentConfig(prev => ({
        ...prev,
        actions: [...prev.actions, newAction]
      }));
    }
    
    setDraggedAction(null);
  };

  const runSampleTest = async () => {
    const testRun = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      status: 'running',
      steps: [],
      context: {
        clientId: 'DEMO_CLIENT_001',
        period: 'December 2024',
        transactionCount: 247
      }
    };
    
    setSampleRuns([testRun, ...sampleRuns]);
    
    // Simulate step execution
    for (let i = 0; i < agentConfig.actions.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const step = {
        actionId: agentConfig.actions[i].id,
        status: 'completed',
        duration: Math.random() * 2000 + 500,
        output: `Sample output for ${agentConfig.actions[i].name}`,
        timestamp: new Date().toISOString()
      };
      
      testRun.steps.push(step);
      setSampleRuns([testRun, ...sampleRuns.slice(1)]);
      
      // Add debug log
      setDebugLog(prev => [...prev, {
        timestamp: new Date().toISOString(),
        level: 'info',
        message: `Completed step: ${agentConfig.actions[i].name}`,
        data: step
      }]);
    }
    
    testRun.status = 'completed';
    setSampleRuns([testRun, ...sampleRuns.slice(1)]);
  };

  const autoArrangeActions = () => {
    const arrangedActions = agentConfig.actions.map((action, index) => ({
      ...action,
      position: {
        x: 50,
        y: 50 + (index * 120) // Space them vertically
      }
    }));
    
    setAgentConfig({
      ...agentConfig,
      actions: arrangedActions
    });
  };

  const ActionPalette = () => (
    <div className={styles.actionPalette}>
      <h3>Action Library</h3>
      
      {['client', 'data', 'ai', 'tax', 'accounting', 'compliance', 'output', 'workflow'].map(category => (
        <div key={category} className={styles.actionCategory}>
          <h4>{category}</h4>
          <div className={styles.actionCategoryItems}>
            {actionTypes.filter(action => action.category === category).map(action => {
              return (
                <div
                  key={action.id}
                  className={styles.actionItem}
                  onMouseDown={(e) => {
                    handleCustomDragStart(e, action);
                  }}
                  style={{
                    userSelect: 'none',
                    MozUserSelect: 'none',
                    WebkitUserSelect: 'none',
                    msUserSelect: 'none',
                    cursor: 'grab'
                  }}
                >
                  <div className={styles.actionItemContent}>
                    <action.icon className={`${styles.iconMd} ${styles.iconGray}`} />
                    <div>
                      <div className={styles.actionItemName}>{action.name}</div>
                      <div className={styles.actionItemDescription}>{action.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  const WorkflowCanvas = () => (
    <div className={styles.workflowCanvasContainer}>
      {agentConfig.actions.length > 0 && (
        <div className={styles.canvasToolbar}>
          <button 
            onClick={autoArrangeActions}
            className={`${styles.btn} ${styles.btnSm}`}
            title="Auto-arrange actions"
          >
            <GitBranch className={styles.iconSm} />
            <span>Arrange</span>
          </button>
          <button 
            onClick={() => setAgentConfig({...agentConfig, actions: []})}
            className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`}
            title="Clear all actions"
          >
            <Trash2 className={styles.iconSm} />
            <span>Clear</span>
          </button>
        </div>
      )}
      <div
        ref={canvasRef}
        className={`${styles.workflowCanvas} ${isDragOver ? styles.dragOver : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="workflow-canvas"
      >
        {agentConfig.actions.length === 0 ? (
          <div className={styles.canvasEmptyState}>
            <div className={styles.canvasEmptyContent}>
              <GitBranch className={styles.icon2xl} />
              <p>Drag actions from the left panel to build your workflow</p>
            </div>
          </div>
        ) : (
          <>
            {agentConfig.actions.map((action, index) => (
              <ActionNode key={action.id} action={action} index={index} />
            ))}
            {/* Enhanced Workflow connections */}
            <svg className={`${styles.absolute} ${styles.inset0} ${styles.pointerEventsNone}`}>
              {agentConfig.actions.map((action, index) => {
                if (index === agentConfig.actions.length - 1) return null;
                const nextAction = agentConfig.actions[index + 1];
                
                // Calculate connection points (from right edge of current to left edge of next)
                const x1 = action.position.x + 240; // Width of action node
                const y1 = action.position.y + 45; // Center height of action node
                const x2 = nextAction.position.x;
                const y2 = nextAction.position.y + 45;
                
                // Create a curved path for better visual flow
                const midX = (x1 + x2) / 2;
                const pathData = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
                
                return (
                  <path
                    key={`${action.id}-${nextAction.id}`}
                    d={pathData}
                    className={styles.flowLine}
                    markerEnd="url(#arrowhead)"
                  />
                );
              })}
              <defs>
                <marker 
                  id="arrowhead" 
                  markerWidth="10" 
                  markerHeight="7" 
                  refX="9" 
                  refY="3.5" 
                  orient="auto"
                  fill="#3b82f6"
                >
                  <polygon points="0 0, 10 3.5, 0 7" />
                </marker>
              </defs>
            </svg>
          </>
        )}
      </div>
    </div>
  );

  const ActionNode = ({ action, index }) => {
    const [showConfig, setShowConfig] = useState(false);
    const ActionIcon = actionTypes.find(t => t.id === action.type)?.icon || Settings;

    const handleDeleteAction = () => {
      setAgentConfig({
        ...agentConfig,
        actions: agentConfig.actions.filter(a => a.id !== action.id)
      });
    };

    return (
      <>
        <div
          className={styles.actionNode}
          style={{ 
            left: action.position.x, 
            top: action.position.y,
          }}
        >
          <div className={styles.actionNodeHeader}>
            <div className={styles.actionNodeLeft}>
              <div className={styles.actionNodeNumber}>
                {index + 1}
              </div>
              <ActionIcon className={`${styles.iconMd} ${styles.iconGray}`} />
              <span className={styles.actionNodeName}>{action.name}</span>
            </div>
            <div className={styles.actionNodeRight}>
              {action.humanCheckpoint && (
                <div className={styles.actionNodeCheckpoint}>
                  <Users className={styles.iconSm} />
                </div>
              )}
              <button
                onClick={() => setShowConfig(!showConfig)}
                className={styles.actionNodeSettings}
                title="Configure"
              >
                <Settings className={styles.iconSm} />
              </button>
              <button
                onClick={handleDeleteAction}
                className={`${styles.actionNodeSettings} ${styles.deleteButton}`}
                title="Delete"
              >
                <X className={styles.iconSm} />
              </button>
            </div>
          </div>
          
          {showConfig && (
            <ActionConfiguration action={action} />
          )}
        </div>
      </>
    );
  };

  const ActionConfiguration = ({ action }) => {
    const [localConfig, setLocalConfig] = useState(action.config);

    return (
      <div className={styles.actionConfig}>
        {action.type === 'ai_analysis' && (
          <AIAnalysisConfig config={localConfig} setConfig={setLocalConfig} />
        )}
        {action.type === 'human_review' && (
          <HumanReviewConfig config={localConfig} setConfig={setLocalConfig} />
        )}
        {action.type === 'transaction_update' && (
          <TransactionUpdateConfig config={localConfig} setConfig={setLocalConfig} />
        )}
        {action.type === 'retrieve_financial' && (
          <FinancialDataConfig config={localConfig} setConfig={setLocalConfig} />
        )}
        
        <div className={styles.actionConfigCheckbox}>
          <input
            type="checkbox"
            id={`human-${action.id}`}
            checked={action.humanCheckpoint}
            onChange={(e) => {
              action.humanCheckpoint = e.target.checked;
              setAgentConfig({...agentConfig});
            }}
          />
          <label htmlFor={`human-${action.id}`}>
            Require human approval before proceeding
          </label>
        </div>
      </div>
    );
  };

  const AIAnalysisConfig = ({ config, setConfig }) => (
    <div className={styles.aiConfigContainer}>
      <div className={styles.actionConfigSection}>
        <label>Canned Prompt</label>
        <select 
          className={styles.actionConfigSelect}
          onChange={(e) => {
            const prompt = cannedPrompts[e.target.value];
            if (prompt) {
              setConfig({...config, userPrompt: prompt.prompt});
            }
          }}
        >
          <option value="">Select a template...</option>
          {Object.entries(cannedPrompts).map(([key, prompt]) => (
            <option key={key} value={key}>{prompt.name}</option>
          ))}
        </select>
      </div>
      <div className={styles.actionConfigSection}>
        <label>Custom Prompt</label>
        <textarea
          rows={4}
          value={config.userPrompt || ''}
          onChange={(e) => setConfig({...config, userPrompt: e.target.value})}
          placeholder="Enter your AI prompt here. Use {{variable}} for dynamic data..."
        />
      </div>
      <div className={styles.actionConfigGrid}>
        <div className={styles.actionConfigSection}>
          <label>Temperature</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={config.temperature || 0.1}
            onChange={(e) => setConfig({...config, temperature: parseFloat(e.target.value)})}
          />
          <span className={styles.rangeValue}>{config.temperature || 0.1}</span>
        </div>
        <div className={styles.actionConfigSection}>
          <label>Output Format</label>
          <select 
            value={config.outputFormat || 'structured'}
            onChange={(e) => setConfig({...config, outputFormat: e.target.value})}
          >
            <option value="structured">Structured JSON</option>
            <option value="narrative">Narrative Text</option>
            <option value="table">Table Format</option>
          </select>
        </div>
      </div>
    </div>
  );

  const HumanReviewConfig = ({ config, setConfig }) => (
    <div className={styles.humanConfigContainer}>
      <div className={styles.actionConfigSection}>
        <label>Review Type</label>
        <select 
          value={config.reviewType || ''}
          onChange={(e) => setConfig({...config, reviewType: e.target.value})}
        >
          <option value="">Select review type...</option>
          <option value="transaction_approval">Transaction Approval</option>
          <option value="client_communication">Client Communication Review</option>
          <option value="tax_position">Tax Position Review</option>
          <option value="financial_analysis">Financial Analysis Review</option>
        </select>
      </div>
      <div className={styles.actionConfigSection}>
        <label>Instructions for Reviewer</label>
        <textarea
          rows={3}
          value={config.instructions || ''}
          onChange={(e) => setConfig({...config, instructions: e.target.value})}
          placeholder="Provide specific instructions for the reviewer..."
        />
      </div>
      <div className={styles.actionConfigSection}>
        <label>Assign to</label>
        <select>
          <option>Senior Accountant</option>
          <option>Tax Manager</option>
          <option>Partner</option>
          <option>Specific User...</option>
        </select>
      </div>
    </div>
  );

  const TransactionUpdateConfig = ({ config, setConfig }) => (
    <div className={styles.transactionConfigContainer}>
      <div className={styles.actionConfigSection}>
        <label>Update Type</label>
        <select 
          value={config.updateType || ''}
          onChange={(e) => setConfig({...config, updateType: e.target.value})}
        >
          <option value="">Select update type...</option>
          <option value="classification">Account Classification</option>
          <option value="tax_codes">Tax Codes</option>
          <option value="client_codes">Client/Project Codes</option>
          <option value="descriptions">Transaction Descriptions</option>
        </select>
      </div>
      <div className={styles.actionConfigSection}>
        <label>Batch Size</label>
        <input
          type="number"
          value={config.batchSize || 50}
          onChange={(e) => setConfig({...config, batchSize: parseInt(e.target.value)})}
        />
      </div>
      <div className={styles.actionConfigSection}>
        <label>Confirmation Required</label>
        <div className={styles.actionConfigCheckbox}>
          <input
            type="checkbox"
            checked={config.requireConfirmation || false}
            onChange={(e) => setConfig({...config, requireConfirmation: e.target.checked})}
          />
          <span>Require confirmation before bulk updates</span>
        </div>
      </div>
    </div>
  );

  const FinancialDataConfig = ({ config, setConfig }) => (
    <div className={styles.financialConfigContainer}>
      <div className={styles.actionConfigSection}>
        <label>Report Type</label>
        <select 
          value={config.reportType || ''}
          onChange={(e) => setConfig({...config, reportType: e.target.value})}
        >
          <option value="">Select report...</option>
          <option value="trial_balance">Trial Balance</option>
          <option value="profit_loss">Profit & Loss</option>
          <option value="balance_sheet">Balance Sheet</option>
          <option value="cash_flow">Cash Flow Statement</option>
          <option value="aged_receivables">Aged Receivables</option>
        </select>
      </div>
      <div className={styles.actionConfigGrid}>
        <div className={styles.actionConfigSection}>
          <label>From Date</label>
          <input
            type="date"
            value={config.startDate || ''}
            onChange={(e) => setConfig({...config, startDate: e.target.value})}
          />
        </div>
        <div className={styles.actionConfigSection}>
          <label>To Date</label>
          <input
            type="date"
            value={config.endDate || ''}
            onChange={(e) => setConfig({...config, endDate: e.target.value})}
          />
        </div>
      </div>
    </div>
  );

  const SampleRunsTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.sampleRunsHeader}>
        <h3>Sample Runs & Testing</h3>
        <button
          onClick={runSampleTest}
          className={`${styles.btn} ${styles.btnGreen}`}
        >
          <Play className={styles.iconMd} />
          <span>Run Test</span>
        </button>
      </div>

      <div className={styles.sampleRunsList}>
        {sampleRuns.map((run) => (
          <div key={run.id} className={styles.sampleRunCard}>
            <div className={styles.sampleRunHeader}>
              <div className={styles.sampleRunLeft}>
                <div className={`${styles.runStatusIndicator} ${
                  run.status === 'running' ? styles.runStatusRunning :
                  run.status === 'completed' ? styles.runStatusCompleted : styles.runStatusFailed
                }`} />
                <span>Test Run #{run.id}</span>
                <span className={styles.sampleRunTimestamp}>
                  {new Date(run.timestamp).toLocaleString()}
                </span>
              </div>
              <span className={`${styles.runStatusBadge} ${
                run.status === 'running' ? styles.runStatusBadgeRunning :
                run.status === 'completed' ? styles.runStatusBadgeCompleted :
                styles.runStatusBadgeFailed
              }`}>
                {run.status}
              </span>
            </div>

            <div className={styles.sampleRunContext}>
              <div>
                <span>Client:</span> {run.context.clientId}
              </div>
              <div>
                <span>Period:</span> {run.context.period}
              </div>
              <div>
                <span>Transactions:</span> {run.context.transactionCount}
              </div>
            </div>

            {run.steps.length > 0 && (
              <div className={styles.sampleRunSteps}>
                <h4>Execution Steps:</h4>
                {run.steps.map((step, index) => (
                  <div key={index} className={styles.sampleRunStep}>
                    <CheckCircle />
                    <span>{actionTypes.find(a => a.id === step.actionId.split('_')[0])?.name}</span>
                    <span className={styles.sampleRunStepDuration}>({step.duration}ms)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const DebugTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.debugHeader}>
        <h3>Debug Console</h3>
        <button
          onClick={() => setDebugLog([])}
          className={styles.btn}
        >
          Clear Log
        </button>
      </div>

      <div className={styles.debugConsole}>
        {debugLog.map((log, index) => (
          <div key={index} className={styles.debugLogEntry}>
            <span className={styles.debugTimestamp}>[{new Date(log.timestamp).toLocaleTimeString()}]</span>
            <span className={`${styles.debugLevel} ${
              log.level === 'error' ? styles.debugLevelError :
              log.level === 'warn' ? styles.debugLevelWarn : styles.debugLevelInfo
            }`}>
              {log.level.toUpperCase()}
            </span>
            <span className={styles.debugMessage}>{log.message}</span>
          </div>
        ))}
        {debugLog.length === 0 && (
          <div className={styles.debugEmpty}>No debug logs yet. Run a test to see execution details.</div>
        )}
      </div>
    </div>
  );

  const ContextTab = () => (
    <div className={styles.tabContent}>
      <h3>Agent Context & Settings</h3>
      
      <div className={styles.contextSections}>
        <div>
          <h4>Client Data Access</h4>
          <div className={styles.contextCheckboxes}>
            <label>
              <input 
                type="checkbox" 
                checked={agentConfig.context.clientData}
                onChange={(e) => setAgentConfig({
                  ...agentConfig,
                  context: {...agentConfig.context, clientData: e.target.checked}
                })}
              />
              <span>Access client financial data</span>
            </label>
            <label>
              <input type="checkbox" />
              <span>Access client communication history</span>
            </label>
            <label>
              <input type="checkbox" />
              <span>Access prior year data for comparisons</span>
            </label>
          </div>
        </div>

        <div>
          <h4>Practice Areas</h4>
          <div className={styles.contextPracticeAreas}>
            {['Tax Preparation', 'Bookkeeping', 'Audit', 'Payroll', 'Advisory', 'Compliance'].map(area => (
              <label key={area}>
                <input type="checkbox" />
                <span>{area}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h4>Human-in-the-Loop Configuration</h4>
          <div className={styles.hitlConfigContainer}>
            <label>
              <input 
                type="checkbox"
                checked={agentConfig.humanInLoop.enabled}
                onChange={(e) => setAgentConfig({
                  ...agentConfig,
                  humanInLoop: {...agentConfig.humanInLoop, enabled: e.target.checked}
                })}
              />
              <span>Enable human review checkpoints</span>
            </label>
            
            <div className={styles.contextInputGroup}>
              <label>Notification Email</label>
              <input
                type="email"
                value={agentConfig.humanInLoop.notificationEmail}
                onChange={(e) => setAgentConfig({
                  ...agentConfig,
                  humanInLoop: {...agentConfig.humanInLoop, notificationEmail: e.target.value}
                })}
                placeholder="reviewer@firm.com"
              />
            </div>

            <div className={styles.contextInputGroup}>
              <label>Review Timeout (hours)</label>
              <input
                type="number"
                value={agentConfig.humanInLoop.timeout || 24}
                onChange={(e) => setAgentConfig({
                  ...agentConfig,
                  humanInLoop: {...agentConfig.humanInLoop, timeout: parseInt(e.target.value)}
                })}
              />
            </div>

            <div className={styles.contextInputGroup}>
              <label>Auto-approve conditions</label>
              <textarea
                rows={3}
                placeholder="e.g., Transactions under $100, Recurring vendor payments"
                value={agentConfig.humanInLoop.autoApproveConditions || ''}
                onChange={(e) => setAgentConfig({
                  ...agentConfig,
                  humanInLoop: {...agentConfig.humanInLoop, autoApproveConditions: e.target.value}
                })}
              />
            </div>
          </div>
        </div>

        <div>
          <h4>Variable Context</h4>
          <div className={styles.contextVariables}>
            <div className={styles.contextVariablesTitle}>Available variables for AI prompts:</div>
            <div className={styles.contextVariablesGrid}>
              <div>{'{{client_name}}'}</div>
              <div>{'{{financial_period}}'}</div>
              <div>{'{{chart_of_accounts}}'}</div>
              <div>{'{{prior_period_data}}'}</div>
              <div>{'{{industry_type}}'}</div>
              <div>{'{{tax_year}}'}</div>
              <div>{'{{transaction_data}}'}</div>
              <div>{'{{budget_data}}'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const HumanApprovalModal = ({ isOpen, onClose, approvalData }) => {
    if (!isOpen) return null;

    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div className={styles.modalHeaderContent}>
              <h3>Human Review Required</h3>
              <button onClick={onClose} className={styles.modalClose}>
                <X className={styles.iconLg} />
              </button>
            </div>
          </div>

          <div className={styles.modalBody}>
            <div className={styles.modalAlert}>
              <div className={styles.modalAlertHeader}>
                <AlertTriangle />
                <span className={styles.modalAlertTitle}>Review Required</span>
              </div>
              <p className={styles.modalAlertText}>
                The AI agent has paused at a human review checkpoint. Please review the following data and decide how to proceed.
              </p>
            </div>

            <div>
              <h4>Action: Transaction Classification</h4>
              <div className={styles.modalDataGrid}>
                <div className={styles.modalDataItem}>
                  <span>Transaction:</span>
                  <div>Office Supplies - $247.83</div>
                </div>
                <div className={styles.modalDataItem}>
                  <span>Suggested Account:</span>
                  <div>Office Expenses (6010)</div>
                </div>
                <div className={styles.modalDataItem}>
                  <span>Confidence:</span>
                  <div>85%</div>
                </div>
                <div className={styles.modalDataItem}>
                  <span>AI Reasoning:</span>
                  <div>Vendor is known office supply company</div>
                </div>
              </div>
            </div>

            <div>
              <h4>Additional Context</h4>
              <ul className={styles.modalContextList}>
                <li>• Client: ABC Manufacturing Corp</li>
                <li>• Period: December 2024</li>
                <li>• Similar transactions: 12 this month</li>
                <li>• Budget impact: Within normal range</li>
              </ul>
            </div>

            <div>
              <h4>Your Decision</h4>
              <div className={styles.modalRadioGroup}>
                <label>
                  <input type="radio" name="decision" value="approve" />
                  <span>Approve AI suggestion</span>
                </label>
                <label>
                  <input type="radio" name="decision" value="modify" />
                  <span>Modify classification</span>
                </label>
                <label>
                  <input type="radio" name="decision" value="reject" />
                  <span>Reject and flag for manual review</span>
                </label>
              </div>
            </div>

            <div className={styles.contextInputGroup}>
              <label>Comments (optional)</label>
              <textarea
                rows={3}
                placeholder="Add any notes or instructions..."
              />
            </div>
          </div>

          <div className={styles.modalFooter}>
            <div className={styles.modalFooterLeft}>
              <Clock />
              <span>Response time: 2 minutes</span>
            </div>
            <div className={styles.modalFooterRight}>
              <button
                onClick={onClose}
                className={styles.btn}
              >
                Cancel
              </button>
              <button className={`${styles.btn} ${styles.btnPrimary}`}>
                Submit Decision
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AgentBuilder = () => (
    <div className={styles.aiAgentsFullScreen}>
      <div className={styles.builderHeader}>
        <div>
          <h1>AI Agent Builder</h1>
          <p>Build intelligent accounting workflows</p>
        </div>
        <div className={styles.builderHeaderActions}>
          <button
            onClick={() => setShowAgentBuilder(false)}
            className={styles.btn}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const newAgent = {
                id: Date.now(),
                ...agentConfig,
                status: 'draft',
                lastRun: null,
                runs: 0
              };
              setAgents([...agents, newAgent]);
              setShowAgentBuilder(false);
            }}
            className={`${styles.btn} ${styles.btnPrimary}`}
          >
            <Save className={styles.iconMd} />
            <span>Save Agent</span>
          </button>
        </div>
      </div>

      <div className={styles.builderFormSection}>
        <input
          type="text"
          placeholder="Agent Name (e.g., Monthly Expense Classification)"
          value={agentConfig.name}
          onChange={(e) => setAgentConfig({...agentConfig, name: e.target.value})}
        />
        <textarea
          placeholder="Describe what this agent does..."
          value={agentConfig.description}
          onChange={(e) => setAgentConfig({...agentConfig, description: e.target.value})}
          rows={2}
        />
      </div>

      <div className={styles.tabNav}>
        {[
          { id: 'builder', name: 'Workflow Builder', icon: GitBranch },
          { id: 'runs', name: 'Sample Runs', icon: Play },
          { id: 'debug', name: 'Debug Console', icon: Bug },
          { id: 'context', name: 'Context & Settings', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
          >
            <tab.icon className={styles.iconMd} />
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      <div className={styles.twoColumnLayout}>
        {activeTab === 'builder' && (
          <>
            <ActionPalette />
            <WorkflowCanvas />
          </>
        )}
        {activeTab === 'runs' && <SampleRunsTab />}
        {activeTab === 'debug' && <DebugTab />}
        {activeTab === 'context' && <ContextTab />}
      </div>
    </div>
  );

  const AgentCard = ({ agent }) => (
    <div className={styles.agentCard}>
      <div className={styles.agentCardHeader}>
        <div>
          <h3>{agent.name}</h3>
          <p>{agent.description}</p>
        </div>
        <div className={`${styles.agentStatus} ${
          agent.status === 'running' ? styles.agentStatusRunning :
          agent.status === 'completed' ? styles.agentStatusCompleted :
          styles.agentStatusDraft
        }`}>
          {agent.status}
        </div>
      </div>
      
      <div className={styles.agentCardMeta}>
        <span>Trigger: {agent.trigger?.name}</span>
        <span>{agent.actions.length} actions</span>
        <span>Runs: {agent.runs}</span>
      </div>

      <div className={styles.agentCardActions}>
        <button
          onClick={() => {
            setAgents(agents.map(a => 
              a.id === agent.id 
                ? { ...a, status: 'running', lastRun: new Date().toISOString(), runs: a.runs + 1 }
                : a
            ));
            setTimeout(() => {
              setAgents(prev => prev.map(a => 
                a.id === agent.id ? { ...a, status: 'completed' } : a
              ));
            }, 3000);
          }}
          disabled={agent.status === 'running'}
          className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
        >
          <Play className={styles.iconSm} />
          <span>Run</span>
        </button>
        <button 
          onClick={() => {
            setAgentConfig(agent);
            setShowAgentBuilder(true);
          }}
          className={`${styles.btn} ${styles.btnSm}`}
        >
          <Edit3 className={styles.iconSm} />
          <span>Edit</span>
        </button>
        <button className={`${styles.btn} ${styles.btnSm}`}>
          <Eye className={styles.iconSm} />
          <span>View</span>
        </button>
        {agent.humanInLoop?.enabled && (
          <div className={styles.humanReviewIndicator}>
            <Users />
            <span>Human Review</span>
          </div>
        )}
      </div>
    </div>
  );

  if (showAgentBuilder) {
    return <AgentBuilder />;
  }

  return (
    <div className={styles.aiAgentsContainer}>
      <div className={styles.aiAgentsHeader}>
        <div className={styles.aiAgentsHeaderContent}>
          <h1>Accounting AI Orchestrator</h1>
          <p>Intelligent automation for accounting firms</p>
        </div>
        <button
          onClick={() => setShowAgentBuilder(true)}
          className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`}
        >
          <Plus className={styles.iconLg} />
          <span>Create Agent</span>
        </button>
      </div>

      <div className={styles.statsGrid}>
        <div className={`${styles.statsCard} ${styles.statsCardBlue}`}>
          <div className={styles.statsCardHeader}>
            <h3>Active Agents</h3>
            <Zap className={styles.iconXl} />
          </div>
          <div className={styles.statValue}>{agents.filter(a => a.status !== 'draft').length}</div>
          <p className={styles.statLabel}>Running automations</p>
        </div>
        
        <div className={`${styles.statsCard} ${styles.statsCardGreen}`}>
          <div className={styles.statsCardHeader}>
            <h3>Transactions Processed</h3>
            <TrendingUp className={styles.iconXl} />
          </div>
          <div className={styles.statValue}>2,847</div>
          <p className={styles.statLabel}>This month</p>
        </div>
        
        <div className={`${styles.statsCard} ${styles.statsCardPurple}`}>
          <div className={styles.statsCardHeader}>
            <h3>Time Saved</h3>
            <Clock className={styles.iconXl} />
          </div>
          <div className={styles.statValue}>127</div>
          <p className={styles.statLabel}>Hours this month</p>
        </div>
      </div>

      {agents.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <Calculator />
          </div>
          <h3>No AI Agents Yet</h3>
          <p>Create your first accounting automation agent</p>
          <button
            onClick={() => setShowAgentBuilder(true)}
            className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`}
          >
            Get Started
          </button>
        </div>
      ) : (
        <div className={styles.agentsGrid}>
          {agents.map((agent) => (
            <AgentCard key={agent.id} agent={agent} />
          ))}
        </div>
      )}

      <HumanApprovalModal 
        isOpen={false} 
        onClose={() => {}} 
        approvalData={{}}
      />
    </div>
  );
};

export default Agents;
